import type { BacktestClosedTrade } from "@/lib/backtest-stats";
import { historyStart, type HistoryPreset } from "@/lib/new-backtest";

/**
 * Derived views for the backtest page. The backend returns trades and summary
 * stats only (no price series), so buy & hold is sampled at trade fills and
 * starts at the first entry price.
 */

export type PerfPoint = {
  t: number;
  /** Strategy balance */
  equity: number;
  /** Buy & hold balance, scaled to the same starting capital */
  hold: number | null;
  /** Drawdown from the running peak, in % (≤ 0) */
  dd: number;
};

function ms(v: string | null | undefined): number | null {
  if (!v) return null;
  const s = /[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : v.replace(" ", "T") + "Z";
  const n = Date.parse(s);
  return Number.isFinite(n) ? n : null;
}

/** Equity, buy & hold and drawdown sampled at every closed trade. */
export function performanceSeries(
  closed: BacktestClosedTrade[],
  fiatStart: number,
  range: { start?: string | null; end?: string | null } = {}
): PerfPoint[] {
  const trades = closed.filter((t) => !t.is_open);
  if (!trades.length || !(fiatStart > 0)) return [];
  const firstPrice = trades[0]!.entry_price;
  const holdAt = (price: number) => (firstPrice > 0 ? (fiatStart * price) / firstPrice : null);

  const raw: Omit<PerfPoint, "dd">[] = [];
  // Anchor at the range start, but never after the first trade.
  const firstEntry = ms(trades[0]!.entry_date);
  const rangeStart = ms(range.start);
  const startMs = rangeStart != null && firstEntry != null ? Math.min(rangeStart, firstEntry) : rangeStart ?? firstEntry;
  if (startMs != null) raw.push({ t: startMs, equity: fiatStart, hold: fiatStart });
  let equity = fiatStart;
  for (const tr of trades) {
    const t = ms(tr.exit_date);
    if (t == null) continue;
    equity = Number.isFinite(tr.balance) && tr.balance !== 0 ? tr.balance : equity + tr.pnl;
    raw.push({ t, equity, hold: holdAt(tr.exit_price) });
  }
  raw.sort((a, b) => a.t - b.t);
  const endMs = ms(range.end);
  const last = raw[raw.length - 1];
  if (endMs != null && last && endMs > last.t) raw.push({ t: endMs, equity: last.equity, hold: last.hold });

  let peak = -Infinity;
  return raw.map((p) => {
    peak = Math.max(peak, p.equity);
    return { ...p, dd: peak > 0 ? ((p.equity - peak) / peak) * 100 : 0 };
  });
}

/** Buy & hold return in %, from the first entry price to the last close. */
export function buyAndHoldPct(closed: BacktestClosedTrade[], lastClose: number | null | undefined): number | null {
  const first = closed[0]?.entry_price;
  if (!first || !lastClose || !Number.isFinite(lastClose)) return null;
  return ((lastClose - first) / first) * 100;
}

/** Date (ms) of the deepest drawdown, for the "Max drawdown" caption. */
export function maxDrawdownAt(series: PerfPoint[]): number | null {
  let worst: PerfPoint | null = null;
  for (const p of series) if (!worst || p.dd < worst.dd) worst = p;
  return worst && worst.dd < 0 ? worst.t : null;
}

export type YearReturn = { year: number; pct: number; partial: boolean };

/** Strategy return per calendar year (UTC), compounding on the year-start balance. */
export function returnsByYear(series: PerfPoint[]): YearReturn[] {
  if (series.length < 2) return [];
  const out: YearReturn[] = [];
  const yearOf = (t: number) => new Date(t).getUTCFullYear();
  let year = yearOf(series[0]!.t);
  let open = series[0]!.equity;
  let close = open;
  const push = () => out.push({ year, pct: open > 0 ? ((close - open) / open) * 100 : 0, partial: false });
  for (const p of series) {
    const y = yearOf(p.t);
    if (y !== year) {
      push();
      for (let gap = year + 1; gap < y; gap++) out.push({ year: gap, pct: 0, partial: false });
      year = y;
      open = close;
    }
    close = p.equity;
  }
  push();
  const first = new Date(series[0]!.t);
  const last = new Date(series[series.length - 1]!.t);
  if (first.getUTCMonth() > 0 || first.getUTCDate() > 1) out[0]!.partial = true;
  if (last.getUTCMonth() < 11 || last.getUTCDate() < 31) out[out.length - 1]!.partial = true;
  return out;
}

export type MonthReturn = { year: number; month: number; pct: number; partial: boolean };

/** Strategy return per calendar month (UTC, month 0–11), compounding on the month-start balance. */
export function returnsByMonth(series: PerfPoint[]): MonthReturn[] {
  if (series.length < 2) return [];
  const out: MonthReturn[] = [];
  // Months since year 0, so stepping across a year boundary is +1.
  const idx = (t: number) => {
    const d = new Date(t);
    return d.getUTCFullYear() * 12 + d.getUTCMonth();
  };
  let cur = idx(series[0]!.t);
  let open = series[0]!.equity;
  let close = open;
  const push = (i: number, pct: number) => out.push({ year: Math.floor(i / 12), month: i % 12, pct, partial: false });
  for (const p of series) {
    const i = idx(p.t);
    if (i !== cur) {
      push(cur, open > 0 ? ((close - open) / open) * 100 : 0);
      for (let gap = cur + 1; gap < i; gap++) push(gap, 0);
      cur = i;
      open = close;
    }
    close = p.equity;
  }
  push(cur, open > 0 ? ((close - open) / open) * 100 : 0);
  const first = new Date(series[0]!.t);
  const last = new Date(series[series.length - 1]!.t);
  const lastDay = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 1, 0)).getUTCDate();
  if (first.getUTCDate() > 1) out[0]!.partial = true;
  if (last.getUTCDate() < lastDay) out[out.length - 1]!.partial = true;
  return out;
}

const PRESETS: Exclude<HistoryPreset, "custom" | "YTD">[] = ["3M", "6M", "1Y"];
const PRESET_TOLERANCE_MS = 7 * 86_400_000;

/**
 * Which range preset a saved start/end matches, else "custom". The current
 * year must start exactly on 1 January; the rolling presets allow a week of
 * drift, since they were counted back from the day the backtest was saved.
 */
export function rangePresetOf(start: string, end: string | null | undefined, now = new Date()): HistoryPreset {
  if (end) return "custom";
  const s = ms(start);
  if (s == null) return "custom";
  if (s === ms(historyStart("YTD", now))) return "YTD";
  for (const p of PRESETS) {
    const want = ms(historyStart(p, now));
    if (want != null && Math.abs(want - s) <= PRESET_TOLERANCE_MS) return p;
  }
  return "custom";
}
